import React from "react";
import { ArrowUp } from "lucide-react";
import { useLocale } from "../../context/LocaleContext";

export type QuestionAskerStatus = { title?: string; status?: string } | null;

export type QuestionAskerMode = "QUESTION" | "ANSWER";

export interface QuestionAskerProps {
  mode: QuestionAskerMode;
  question: string;
  inputValue: string;
  onInputValueChange: (next: string) => void;
  onSubmit: () => void;
  onAskSuggestion?: (text: string) => void;
  suggestions?: string[];
  loading: boolean;
  status: QuestionAskerStatus;
  answerMarkdown: string;
  onNewQuestion: () => void;
  className?: string;
}

type QaOverflowMode = "scroll-fade" | "expand-collapse";
type QaDisplayConfig = {
  showTitle: boolean;
  showSubtitle: boolean;
  showEntryPoweredBy: boolean;
  showQuestionWas: boolean;
  showQuestion: boolean;
};

// Read theme CSS variables before the browser paints so the widget never shows
// its default (generic) structure for a frame before the merchant theme corrects
// it. Falls back to useEffect during SSR where useLayoutEffect is unavailable.
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

function parseThemeStringToken(rawValue: string): string {
  const trimmed = rawValue.trim();
  if (!trimmed) return "";
  const hasMatchingQuotes =
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"));
  if (hasMatchingQuotes) {
    return trimmed.slice(1, -1).trim();
  }
  return trimmed;
}

export function QuestionAsker({
  mode,
  question,
  inputValue,
  onInputValueChange,
  onSubmit,
  onAskSuggestion,
  suggestions = [],
  loading,
  answerMarkdown,
  onNewQuestion,
  className,
}: QuestionAskerProps) {
  const { t, language } = useLocale();
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const collapsedTextRef = React.useRef<HTMLDivElement | null>(null);
  const [qaOverflowMode, setQaOverflowMode] = React.useState<QaOverflowMode>("scroll-fade");
  const [qaDisplayConfig, setQaDisplayConfig] = React.useState<QaDisplayConfig>({
    showTitle: true,
    showSubtitle: true,
    showEntryPoweredBy: true,
    showQuestionWas: true,
    showQuestion: true,
  });
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [hasOverflow, setHasOverflow] = React.useState(false);
  const [hasInputUnderline, setHasInputUnderline] = React.useState(true);
  const [themePlaceholder, setThemePlaceholder] = React.useState("");
  const [placeholderPhrases, setPlaceholderPhrases] = React.useState<string[]>([]);
  const [animatedPlaceholder, setAnimatedPlaceholder] = React.useState("");
  const COLLAPSED_LINE_COUNT = 1;
  const qaSmallBodyButtonStyle: React.CSSProperties = {
    fontFamily: "var(--type-body-sm-family)",
    fontSize: "var(--type-body-sm-size)",
    fontWeight: "var(--type-body-sm-weight)",
    lineHeight: "var(--type-body-sm-line)",
    letterSpacing: "var(--type-body-sm-tracking)",
    textTransform: "var(--type-body-sm-transform)" as React.CSSProperties["textTransform"],
  };

  useIsomorphicLayoutEffect(() => {
    const readOverflowMode = () => {
      if (!rootRef.current) return;
      const raw = window.getComputedStyle(rootRef.current).getPropertyValue("--ww-qa-overflow-mode");
      const parsed = parseThemeStringToken(raw);
      setQaOverflowMode(parsed === "expand-collapse" ? "expand-collapse" : "scroll-fade");
    };

    // Theme CSS may be applied asynchronously.
    readOverflowMode();
    const rafId = window.requestAnimationFrame(readOverflowMode);
    const timeoutId = window.setTimeout(readOverflowMode, 200);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, []);

  useIsomorphicLayoutEffect(() => {
    const readPlaceholderFromTheme = () => {
      if (!rootRef.current) return;
      const styles = window.getComputedStyle(rootRef.current);
      const normalizedLanguage = String(language || "").toLowerCase();
      const localeTokens =
        normalizedLanguage === "nb" ? ["nb", "no"] :
        normalizedLanguage === "no" ? ["no", "nb"] :
        [normalizedLanguage];

      let resolved = "";
      for (const token of localeTokens) {
        if (!token) continue;
        const raw = styles.getPropertyValue(`--ww-product-qa-placeholder-${token}`);
        const parsed = parseThemeStringToken(raw);
        if (parsed) {
          resolved = parsed;
          break;
        }
      }

      if (!resolved) {
        resolved = parseThemeStringToken(styles.getPropertyValue("--ww-product-qa-placeholder"));
      }
      setThemePlaceholder(resolved);

      let phrases: string[] = [];
      for (const token of localeTokens) {
        if (!token) continue;
        const raw = styles.getPropertyValue(`--ww-product-qa-placeholders-${token}`);
        const parsed = parseThemeStringToken(raw);
        if (parsed) {
          phrases = parsed.split("|").map((s) => s.trim()).filter(Boolean);
          if (phrases.length > 0) break;
        }
      }
      setPlaceholderPhrases((prev) => (prev.join("|") === phrases.join("|") ? prev : phrases));
    };

    // Theme CSS may be applied asynchronously.
    readPlaceholderFromTheme();
    const rafId = window.requestAnimationFrame(readPlaceholderFromTheme);
    const timeoutId = window.setTimeout(readPlaceholderFromTheme, 200);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [language]);

  React.useEffect(() => {
    if (placeholderPhrases.length < 2) {
      setAnimatedPlaceholder("");
      return;
    }

    const reduceMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const TYPE_MS = 55;
    const ERASE_MS = 30;
    const HOLD_MS = 1800;
    const SWAP_MS = 2800;

    let phraseIndex = 0;
    let charIndex = 0;
    let phase: "type" | "hold" | "erase" = "type";
    let timer: number | undefined;

    const tick = () => {
      if (reduceMotion) {
        setAnimatedPlaceholder(placeholderPhrases[phraseIndex]);
        phraseIndex = (phraseIndex + 1) % placeholderPhrases.length;
        timer = window.setTimeout(tick, SWAP_MS);
        return;
      }
      const phrase = placeholderPhrases[phraseIndex];
      if (phase === "type") {
        charIndex++;
        setAnimatedPlaceholder(phrase.slice(0, charIndex));
        if (charIndex >= phrase.length) {
          phase = "hold";
          timer = window.setTimeout(tick, HOLD_MS);
        } else {
          timer = window.setTimeout(tick, TYPE_MS);
        }
      } else if (phase === "hold") {
        phase = "erase";
        timer = window.setTimeout(tick, ERASE_MS);
      } else {
        charIndex--;
        setAnimatedPlaceholder(phrase.slice(0, Math.max(charIndex, 0)));
        if (charIndex <= 0) {
          phase = "type";
          phraseIndex = (phraseIndex + 1) % placeholderPhrases.length;
          timer = window.setTimeout(tick, TYPE_MS);
        } else {
          timer = window.setTimeout(tick, ERASE_MS);
        }
      }
    };

    setAnimatedPlaceholder("");
    timer = window.setTimeout(tick, TYPE_MS);
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [placeholderPhrases]);

  useIsomorphicLayoutEffect(() => {
    const readDisplayConfig = () => {
      if (!rootRef.current) return;
      const styles = window.getComputedStyle(rootRef.current);
      const showWhenNotNone = (cssVarName: string, fallback: string) => {
        const parsed = parseThemeStringToken(styles.getPropertyValue(cssVarName)) || fallback;
        return parsed !== "none";
      };
      setQaDisplayConfig({
        showTitle: showWhenNotNone("--ww-product-qa-title-display", "block"),
        showSubtitle: showWhenNotNone("--ww-product-qa-subtitle-display", "block"),
        showEntryPoweredBy: showWhenNotNone("--ww-product-qa-entry-powered-by-display", "inline"),
        showQuestionWas: showWhenNotNone("--ww-product-qa-question-was-display", "block"),
        showQuestion: showWhenNotNone("--ww-product-qa-question-display", "block"),
      });
      const inputUnderline = parseThemeStringToken(styles.getPropertyValue("--ww-product-qa-input-underline"))
        || "2px solid #e5e7eb";
      setHasInputUnderline(inputUnderline !== "none");
    };

    // Theme CSS may be applied asynchronously.
    readDisplayConfig();
    const rafId = window.requestAnimationFrame(readDisplayConfig);
    const timeoutId = window.setTimeout(readDisplayConfig, 200);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, []);

  React.useEffect(() => {
    if (mode !== "ANSWER" || qaOverflowMode !== "expand-collapse" || loading) {
      setHasOverflow(false);
      return;
    }
    if (isExpanded) {
      // Keep the last collapsed-state overflow result while expanded.
      return;
    }
    const el = collapsedTextRef.current;
    if (!el) {
      setHasOverflow(false);
      return;
    }
    const checkOverflow = () => {
      // Measure hidden vertical overflow from line-clamped collapsed state.
      setHasOverflow(el.scrollHeight > el.clientHeight + 1);
    };
    checkOverflow();
    const rafId = window.requestAnimationFrame(checkOverflow);
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(checkOverflow);
      observer.observe(el);
    }
    return () => {
      window.cancelAnimationFrame(rafId);
      observer?.disconnect();
    };
  }, [mode, qaOverflowMode, loading, answerMarkdown, isExpanded]);

  React.useEffect(() => {
    setIsExpanded(false);
  }, [question, answerMarkdown, mode]);

  return (
    <div ref={rootRef} className={`${className || "w-full"} min-w-0 max-w-full`}>
      <div
        className="rounded-xl bg-white mx-auto w-full min-w-0 max-w-full"
        style={{
          border: "var(--border-width, 2px) solid var(--secondary)",
          fontFamily: "var(--font-body)",
          paddingInline: "var(--ww-product-qa-padding-x, 1rem)",
          paddingBlock: "var(--ww-product-qa-padding-y, 1rem)",
        }}
      >
        {mode === "QUESTION" ? (
          <div className="flex flex-col gap-4">
            {(qaDisplayConfig.showTitle || qaDisplayConfig.showSubtitle) && (
              <div className="flex flex-col gap-2">
                {qaDisplayConfig.showTitle && (
                  <h2 className="ww-type-heading-sm" style={{ color: "var(--text-secondary)" }}>
                    {t("product_qa.title")}
                  </h2>
                )}
                {qaDisplayConfig.showSubtitle && (
                  <p className="ww-type-body-sm" style={{ color: "var(--text-secondary)" }}>
                    {t("product_qa.subtitle")}
                  </p>
                )}
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
              }}
              className="ww-product-qa-form flex items-center gap-3"
            >
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => onInputValueChange(e.target.value)}
                  placeholder={
                    placeholderPhrases.length >= 2
                      ? " "
                      : themePlaceholder || t("product_qa.placeholder")
                  }
                  className={`ww-product-qa-input w-full bg-transparent outline-none ww-type-body-sm ${hasInputUnderline ? "pb-2" : ""}`}
                  style={{ borderBottom: "var(--ww-product-qa-input-underline, 2px solid #e5e7eb)" }}
                  aria-label="Skriv din fråga"
                />
                {placeholderPhrases.length >= 2 && inputValue === "" && (
                  <span
                    aria-hidden="true"
                    className={`ww-product-qa-placeholder-anim ww-type-body-sm pointer-events-none absolute inset-0 flex items-center overflow-hidden whitespace-nowrap ${hasInputUnderline ? "pb-2" : ""}`}
                    style={{ color: "var(--text-tertiary)" }}
                  >
                    {animatedPlaceholder}
                  </span>
                )}
              </div>
              <button
                type="submit"
                aria-label="Skicka"
                className="ww-product-qa-send w-10 h-10 rounded-full flex items-center justify-center ww-btn-primary"
                style={{ display: "var(--ww-product-qa-send-button-display, inline-flex)" }}
              >
                <ArrowUp className="w-[24px] h-[24px]" style={{ color: "inherit" }} />
              </button>
            </form>

            {suggestions.length > 0 && (
              <div className="flex gap-2">
                {suggestions.map((label) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => onAskSuggestion?.(label)}
                    className="ww-btn ww-btn-third"
                    style={qaSmallBodyButtonStyle}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            {qaDisplayConfig.showEntryPoweredBy && (
              <div className="text-center">
                <span className="ww-type-body-sm text-gray-400" style={{ fontSize: "10px" }}>
                  Powered by{" "}
                  <a
                    href="https://www.pakolabs.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-gray-500"
                  >
                    Pako Labs
                  </a>
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="relative flex flex-col gap-4 min-w-0 max-w-full">
            {(qaDisplayConfig.showQuestionWas || qaDisplayConfig.showQuestion) && (
              <div className="flex flex-col gap-1">
                {qaDisplayConfig.showQuestionWas && (
                  <div className="ww-type-body-sm text-gray-500">{t("product_qa.question_was")}</div>
                )}
                {qaDisplayConfig.showQuestion && (
                  <div className="ww-type-body-sm font-medium" style={{ color: "var(--text-secondary)" }}>
                    {question}
                  </div>
                )}
              </div>
            )}

            {qaOverflowMode === "expand-collapse" ? (
              <div className="py-0 min-w-0 max-w-full">
                {loading ? (
                  <div className="flex justify-start">
                    <div className="rounded-3xl inline-flex items-center" aria-live="polite">
                      <span className="ww-type-body-sm ww-qa-thinking">{t("common.thinking")}</span>
                    </div>
                  </div>
                ) : (
                  <div
                    ref={collapsedTextRef}
                    className="ww-type-body-sm min-w-0 w-full max-w-full"
                    style={{
                      color: "var(--text-secondary)",
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      ...(isExpanded
                        ? {}
                        : {
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical" as React.CSSProperties["WebkitBoxOrient"],
                            WebkitLineClamp: COLLAPSED_LINE_COUNT,
                          }),
                    }}
                  >
                    {answerMarkdown || ""}
                  </div>
                )}
              </div>
            ) : (
              <div className="relative">
                {/* Scroll container */}
                <div
                  className="overflow-y-auto max-h-[160px] pr-2"
                  style={{ overscrollBehavior: "contain" }}
                >
                  {/* Content with vertical padding matching fade height */}
                  <div className="py-4">
                    {loading ? (
                      <div className="flex justify-start">
                        <div className="rounded-3xl inline-flex items-center" aria-live="polite">
                          <span className="ww-type-body-sm ww-qa-thinking">{t("common.thinking")}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="ww-type-body-sm whitespace-pre-wrap break-words" style={{ color: "var(--text-secondary)" }}>
                        {answerMarkdown || ""}
                      </div>
                    )}
                  </div>
                </div>

                {/* Top fade overlay (outside scroll container) */}
                <div
                  className="pointer-events-none absolute inset-x-0 top-0 h-6"
                  style={{
                    background: "linear-gradient(to top, rgba(255,255,255,0), rgba(255,255,255,1))",
                  }}
                />

                {/* Bottom fade overlay (outside scroll container) */}
                <div
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-6"
                  style={{
                    background: "linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))",
                  }}
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onNewQuestion}
                  className="ww-btn ww-btn-third"
                  style={qaSmallBodyButtonStyle}
                  aria-label="Ny fråga"
                >
                  {t("product_qa.new_question")}
                </button>
                {qaOverflowMode === "expand-collapse" && !loading && (isExpanded || hasOverflow) && (
                  <button
                    type="button"
                    onClick={() => setIsExpanded((prev) => !prev)}
                    className="ww-btn ww-btn-third"
                    style={qaSmallBodyButtonStyle}
                  >
                    {isExpanded ? t("product_qa.show_less") : t("product_qa.show_more")}
                  </button>
                )}
              </div>
              <div className="ww-type-body-sm text-gray-400" style={{ fontSize: "10px" }}>
                <span>
                  Powered by{" "}
                  <a
                    href="https://www.pakolabs.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-gray-500"
                  >
                    Pako Labs
                  </a>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


